import { CenteredLoader } from '@/components_deprecated/Loader/CenteredLoader'
import { DataTable } from '@/components'
import { Button } from '@/components/ui'
import SalaryGantt from '@/components/kibo-ui/gantt/salary-gantt'
import { enforceHourMinuteString } from '@/modules/activities'
import { LazyRouteMap } from '@/modules/salary/work-period-map/lazy-route-map'
import { useWorkPeriodRoute } from '@/modules/salary/work-period-map/use-work-period-route'
import { WorkPeriodMapDialog } from '@/modules/salary/work-period-map/work-period-map-dialog'
import { WORK_PERIOD } from '@/modules/salary/queries'
import { useQuery } from '@apollo/client'
import { createColumnHelper } from '@tanstack/react-table'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Maximize2 } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import styled from 'styled-components'

/** One line of the work period's activity time summary. */
interface ActivityTimeRow {
  id: string
  activity: string
  value: ReactNode
}

type WorkPeriodHoursAndMapView = 'all' | 'time' | 'map'

const Layout = styled.div<{
  $showMap: boolean
  $view: WorkPeriodHoursAndMapView
}>`
  display: grid;
  grid-template-columns: ${props => {
    if (props.$view !== 'all') return 'minmax(0, 1fr)'
    return props.$showMap
      ? 'minmax(0, 1fr) 2.5rem minmax(320px, 0.55fr)'
      : 'minmax(0, 1fr) 2.5rem'
  }};
  gap: 1rem;
  align-items: start;
  padding: 1rem 0;

  ${props => props.theme.media.mobile} {
    grid-template-columns: 100%;
  }
`

const GanttPanel = styled.div`
  min-width: 0;
  overflow-x: auto;
`

const GanttMeta = styled.div`
  margin-top: 0.25rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
  line-height: 1.35;
`

const VehicleList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
  color: ${props => props.theme.colors.gray3};
  font-size: 0.9rem;
`

const VehicleBadge = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray2};
  font-weight: 600;

  &::before {
    content: '';
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 999px;
    background: ${props => props.$color ?? props.theme.colors.gray6};
  }
`

const MapPanel = styled.div`
  min-width: 0;
`

const MapFrame = styled.div`
  position: relative;
  height: 420px;
  overflow: hidden;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
`

const ExpandButton = styled.button`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray2};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.gray11};
  }
`

const StateBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 420px;
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray3};
  text-align: center;
`

const ToggleColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 420px;

  ${props => props.theme.media.mobile} {
    min-height: auto;
    justify-content: flex-end;
    margin: -0.25rem 0;
  }
`

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 3rem;
  border: 1px solid ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.gray2};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.gray11};
  }
`

export const WorkPeriodHoursAndMap: React.FC<{
  workPeriodId: string
  activitiesOnly?: boolean
  showMap: boolean
  onToggleMap: () => void
  view?: WorkPeriodHoursAndMapView
}> = ({ workPeriodId, activitiesOnly, showMap, onToggleMap, view = 'all' }) => {
  const translations = useTranslate({
    noData: 'common.no-data',
    map: 'common.map',
    openFullMap: 'salary.open-full-map',
    vehicles: 'common.vehicles',
    drivingGrade: 'activities.driving-grade',
    activity: 'common.activity',
    value: 'common.value',
    timeDriving: 'common.driving',
    timePause: 'common.pause',
    timeAvailability: 'common.availability',
    timeOtherWork: 'common.other-work',
    timeWorkTotal: 'common.total-work',
    paid: 'common.paid',
    unpaid: 'common.unpaid',
  })

  const [fullscreen, setFullscreen] = useState(false)

  const { data, loading } = useQuery(WORK_PERIOD, {
    variables: { id: workPeriodId },
  })

  const { routes, usedVehicles, hasCoordinates } = useWorkPeriodRoute(
    data?.workPeriod?.drivenCoordinates,
    data?.workPeriod?.activities,
    data?.workPeriod?.estimateActivities
  )
  const drivingGrade =
    data?.workPeriod?.drivingGrade ??
    data?.workPeriod?.driverGrade?.grade ??
    null

  const activityTimes = data?.workPeriod?.activityTimes
  const showGanttPanel = view !== 'map'
  const showMapPanel = view === 'map' || (view === 'all' && showMap)

  const activityTimeColumn = createColumnHelper<ActivityTimeRow>()
  const activityTimeColumns = useMemo(
    () => [
      activityTimeColumn.accessor('activity', {
        header: translations.activity,
      }),
      activityTimeColumn.display({
        id: 'value',
        header: translations.value,
        meta: { end: true },
        cell: info => info.row.original.value,
      }),
    ],
    [activityTimeColumn, translations.activity, translations.value]
  )

  const activityTimeRows = useMemo<ActivityTimeRow[]>(() => {
    if (!activityTimes) return []

    const value = (duration: string | null | undefined) => (
      <span className="font-semibold">{enforceHourMinuteString(duration)}</span>
    )

    return [
      {
        id: 'time-work-total',
        activity: translations.timeWorkTotal,
        value: value(activityTimes.timeWorkTotal),
      },
      {
        id: 'time-driving',
        activity: translations.timeDriving,
        value: value(activityTimes.timeDriving),
      },
      {
        id: 'time-other-work',
        activity: translations.timeOtherWork,
        value: value(activityTimes.timeOtherWork),
      },
      {
        id: 'time-availability',
        activity: translations.timeAvailability,
        value: value(activityTimes.timeAvailability),
      },
      {
        id: 'time-pause',
        activity: translations.timePause,
        value: (
          <>
            {translations.paid}: {value(activityTimes.timePaidRest)}
            <br />
            {translations.unpaid}: {value(activityTimes.timePause)}
          </>
        ),
      },
    ]
  }, [
    activityTimes,
    translations.paid,
    translations.timeAvailability,
    translations.timeDriving,
    translations.timeOtherWork,
    translations.timePause,
    translations.timeWorkTotal,
    translations.unpaid,
  ])

  return (
    <Layout $showMap={showMap} $view={view}>
      {showGanttPanel && (
        <GanttPanel>
          <SalaryGantt
            workPeriodId={workPeriodId}
            activitiesOnly={activitiesOnly}
            fullWidth={view === 'time' || !showMap}
          />
          {drivingGrade && (
            <GanttMeta>
              {translations.drivingGrade}: {drivingGrade}
            </GanttMeta>
          )}
          <DataTable
            className="mt-4"
            columns={activityTimeColumns}
            data={activityTimeRows}
            loading={loading && !activityTimes}
            noDataText={translations.noData}
            inCard
          />
        </GanttPanel>
      )}

      {view === 'all' && (
        <ToggleColumn>
          <ToggleButton
            type="button"
            onClick={onToggleMap}
            title={translations.map}
            aria-label={translations.map}
            aria-expanded={showMap}
          >
            <Icon icon={showMap ? 'chevron-right' : 'chevron-left'} />
          </ToggleButton>
        </ToggleColumn>
      )}

      {showMapPanel && (
        <MapPanel>
          {loading ? (
            <StateBox>
              <CenteredLoader marginTop="0" size={28} thickness={3} />
            </StateBox>
          ) : hasCoordinates ? (
            <MapFrame>
              <LazyRouteMap className="h-full w-full" routes={routes} />
              <ExpandButton
                type="button"
                onClick={() => setFullscreen(true)}
                title={translations.openFullMap}
                aria-label={translations.openFullMap}
              >
                <Maximize2 size={16} />
              </ExpandButton>
            </MapFrame>
          ) : (
            <StateBox>{translations.noData}</StateBox>
          )}
          {!loading && (
            <VehicleList>
              <span>{translations.vehicles}:</span>
              {usedVehicles.length > 0
                ? usedVehicles.map(vehicle => (
                    <VehicleBadge key={vehicle.label} $color={vehicle.color}>
                      {vehicle.label}
                    </VehicleBadge>
                  ))
                : translations.noData}
              {hasCoordinates && (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto bg-white"
                  onClick={() => setFullscreen(true)}
                >
                  <Maximize2 />
                  {translations.openFullMap}
                </Button>
              )}
            </VehicleList>
          )}
          <WorkPeriodMapDialog
            workPeriodId={workPeriodId}
            routes={routes}
            usedVehicles={usedVehicles}
            activitiesOnly={activitiesOnly}
            open={fullscreen}
            onOpenChange={setFullscreen}
          />
        </MapPanel>
      )}
    </Layout>
  )
}
